import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer.ts';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common.ts';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1.ts';
import { ReactQueryFunctionContext } from '@dealstack/shared/lib/libraries/reactQuery.ts';
import type {
	PipelineStatus,
	RichCanonicalEntity,
} from '@dealstack/shared/lib/temporal/documentExtraction/types';
import type {
	ExternalTransaction,
	EnrichedExternalTransaction,
	BatchSecurityMatchStatus,
	BatchPersonMatchStatus,
} from '@dealstack/shared/lib/temporal/documentExtraction/apiTypes';
import { TaskStatus } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';

export type {
	ExternalTransaction,
	EnrichedExternalTransaction,
	BatchSecurityMatchStatus,
	BatchPersonMatchStatus,
};

const get = <T>(
	endpoint: string,
	options?: { signal?: AbortSignal; headers?: Record<string, string> }
): Promise<T> =>
	makeRequest<ApiV1.Response<T>>(
		{ endpoint, method: HTTP_METHOD.GET, customHeaders: options?.headers },
		{ abortSignal: options?.signal }
	).then((r) => r.payload as T);

const post = <T = void>(
	endpoint: string,
	body?: unknown,
	headers?: Record<string, string>
): Promise<T> =>
	makeRequest<ApiV1.Response<T>>({
		endpoint,
		method: HTTP_METHOD.POST,
		body: body != null ? JSON.stringify(body) : undefined,
		customHeaders: headers,
	}).then((r) => r.payload as T);

const patch = (endpoint: string, body: unknown, headers?: Record<string, string>): Promise<void> =>
	makeRequest({
		endpoint,
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(body),
		customHeaders: headers,
	}).then(() => undefined);

export interface StartExtractionDocument {
	fileId: string;
}

export interface StartExtractionRequest {
	dealId: string;
	personId?: string;
	documents: StartExtractionDocument[];
	batchTitle?: string;
	knownSecurities?: RichCanonicalEntity[];
	knownPersons?: RichCanonicalEntity[];
}

export interface StartExtractionResponse {
	taskId: string;
	batchId: string;
}

export interface ExtractionDocumentStatus {
	documentId: string;
	fileName: string;
	workflowId: string;
	status: 'pending' | 'running' | 'completed' | 'failed';
	pipelineStatus?: PipelineStatus;
	error?: string;
}

export interface GetExtractionStatusResponse {
	taskId: string;
	batchId?: string;
	batchTitle?: string;
	status: TaskStatus;
	totalDocuments: number;
	totalProcessed: number;
	documents: ExtractionDocumentStatus[];
	error?: string;
}

export interface ExtractionResultItem {
	documentId: string;
	transactionCount: number;
	fileName: string;
}

export interface GetExtractionResultResponse {
	taskId: string;
	results: ExtractionResultItem[];
}

export const startDocumentExtraction = (
	args: StartExtractionRequest
): Promise<StartExtractionResponse> =>
	post<StartExtractionResponse>(API_PATHS.DOCUMENT_PARSING_START_EXTRACTION_POST, args);

export const getDocumentExtractionStatus = ({
	queryKey,
	signal,
}: ReactQueryFunctionContext): Promise<GetExtractionStatusResponse> =>
	get<GetExtractionStatusResponse>(queryKey[0], {
		signal,
		headers: { 'deal-id': queryKey[1] as string },
	});

export const getDocumentExtractionResult = ({
	queryKey,
	signal,
}: ReactQueryFunctionContext): Promise<GetExtractionResultResponse> =>
	get<GetExtractionResultResponse>(queryKey[0], {
		signal,
		headers: { 'deal-id': queryKey[1] as string },
	});

export interface BatchListDocumentItem {
	id: string;
	workflowId: string;
	fileName: string;
	status: string;
	errorMessage?: string | null;
}

export interface BatchListItem {
	id: string;
	title?: string;
	status: TaskStatus;
	documentCount: number;
	createdAt: string;
	documents: BatchListDocumentItem[];
}

export interface ListBatchesResponse {
	batches: BatchListItem[];
}

export const updateBatchTitle = (batchId: string, title: string, dealId: string): Promise<void> =>
	patch(
		formatUnicorn(API_PATHS.DOCUMENT_PARSING_EXTRACTION_BATCH_BATCHID_TITLE_PATCH, { batchId }),
		{ title },
		{ 'deal-id': dealId }
	);

export interface DocumentTransactionsResponse {
	documentId: string;
	consolidatedTransactions: ExternalTransaction[];
	/** Set when the summary-api call failed — distinguishes "no data" from "API error". */
	fetchError?: string;
}

export const getDocumentTransactions = (
	documentId: string,
	dealId: string
): Promise<DocumentTransactionsResponse> =>
	get<DocumentTransactionsResponse>(
		formatUnicorn(API_PATHS.DOCUMENT_PARSING_DOCUMENT_DOCUMENTID_TRANSACTIONS_GET, {
			documentId,
		}),
		{
			headers: { 'deal-id': dealId },
		}
	);

export interface BatchDocumentTransactions {
	documentId: string;
	fileName: string;
	fileUrl: string | null;
	transactions: ExternalTransaction[];
}

/** A document whose extraction data could not be fetched from the summary-api. */
export interface DocumentFetchError {
	documentId: string;
	fileName: string;
	error: string;
}

export interface GetBatchTransactionsResponse {
	batchId: string;
	documents: BatchDocumentTransactions[];
	fetchErrors?: DocumentFetchError[];
}

export const getBatchTransactions = (
	batchId: string,
	dealId: string,
	signal?: AbortSignal
): Promise<GetBatchTransactionsResponse> =>
	get<GetBatchTransactionsResponse>(
		formatUnicorn(API_PATHS.DOCUMENT_PARSING_BATCH_BATCHID_TRANSACTIONS_GET, { batchId }),
		{
			signal,
			headers: { 'deal-id': dealId },
		}
	);

// --- Batch Analysis Page APIs ---

export interface BatchOverviewResponse {
	batchId: string;
	title: string;
	documentCount: number;
	extractedCount: number;
	partialExtractionCount: number;
	unrelatedCount: number;
	documentTypeCounts: Record<string, number>;
	securitiesCount?: number;
	securitiesMatchedCount?: number;
	securitiesNewCount?: number;
	securitiesUnmatchedCount?: number;
	peopleCount?: number;
	peopleMatchedCount?: number;
	peopleNewCount?: number;
	peopleUnmatchedCount?: number;
	customSystemPrompt?: string | null;
	fetchFailedCount: number;
	fetchErrors?: DocumentFetchError[];
}

export const getBatchOverview = (
	batchId: string,
	dealId: string,
	signal?: AbortSignal
): Promise<BatchOverviewResponse> =>
	get<BatchOverviewResponse>(
		formatUnicorn(API_PATHS.DOCUMENT_PARSING_BATCH_BATCHID_OVERVIEW_GET, { batchId }),
		{
			signal,
			headers: { 'deal-id': dealId },
		}
	);

export interface BatchDocumentItem {
	documentId: string;
	fileName: string;
	fileUrl: string;
	documentType: string;
	transactionCount: number;
	peopleCount: number;
	status: 'extracted' | 'partial_extraction' | 'unrelated' | 'error' | 'merged';
}

export interface GetBatchDocumentsResponse {
	documents: BatchDocumentItem[];
	totalCount: number;
	hasNextPage: boolean;
	documentTypeCounts: Record<string, number>;
}

export const getBatchDocumentsEnriched = (
	batchId: string,
	dealId: string,
	filters: { status?: string; page?: number; limit?: number },
	signal?: AbortSignal
): Promise<GetBatchDocumentsResponse> => {
	const params = new URLSearchParams();
	if (filters.status) {
		params.set('status', filters.status);
	}
	if (filters.page != null) {
		params.set('page', String(filters.page));
	}
	if (filters.limit != null) {
		params.set('limit', String(filters.limit));
	}

	const base = formatUnicorn(API_PATHS.DOCUMENT_PARSING_BATCH_BATCHID_DOCUMENTS_GET, {
		batchId,
	});
	const query = params.toString();
	return get<GetBatchDocumentsResponse>(query ? `${base}?${query}` : base, {
		signal,
		headers: { 'deal-id': dealId },
	});
};

export const getDealBatches = (
	dealId: string,
	signal?: AbortSignal
): Promise<ListBatchesResponse> =>
	get<ListBatchesResponse>(API_PATHS.DOCUMENT_PARSING_DEAL_BATCHES_GET, {
		signal,
		headers: { 'deal-id': dealId },
	});

/**
 * Persists per-extracted-transaction user overrides to the document-parsing
 * batch so the values (e.g. the date typed in the missing-fields modal)
 * survive a page refresh.
 *
 * Keyed by the AI-assigned `transactionId`. Shallow-merged server-side so
 * fields already saved on the same tx are preserved when a partial update
 * arrives.
 */
export interface SaveExtractionOverridesResponse {
	success: boolean;
	/**
	 * Extraction tx ids whose overrides could not be mapped to a document in
	 * the batch (stale after a re-extract) and were therefore not saved.
	 */
	skippedTxIds: string[];
}

export const saveExtractionOverrides = (
	batchId: string,
	dealId: string,
	overrides: Record<
		string,
		{
			transactionDate?: string;
			numberOfUnits?: number;
			priceCostValue?: number;
			sourceName?: string;
			destinationName?: string;
		}
	>
): Promise<SaveExtractionOverridesResponse> =>
	post<SaveExtractionOverridesResponse>(
		formatUnicorn(API_PATHS.DOCUMENT_PARSING_BATCH_BATCHID_MERGE_POST, { batchId }),
		{ overrides },
		{ 'deal-id': dealId }
	);

// --- Batch Securities APIs ---

export interface BatchSecurityItem {
	extractedName: string;
	matchStatus: BatchSecurityMatchStatus;
	transactionCount: number;
	documentCount: number;
	matchedSecurityId: string | null;
	matchedSecurityName: string | null;
	issuerName: string | null;
	issuerPersonId: string | null;
	reviewStatus: 'needs_review' | 'merged';
}

export interface GetBatchSecuritiesResponse {
	securities: BatchSecurityItem[];
	total: number;
	statusCounts: { matched: number; new: number; unmatched: number; potential_match: number };
	fetchErrors?: DocumentFetchError[];
}

export const getBatchSecurities = (
	batchId: string,
	dealId: string,
	signal?: AbortSignal
): Promise<GetBatchSecuritiesResponse> =>
	get<GetBatchSecuritiesResponse>(
		formatUnicorn(API_PATHS.DOCUMENT_PARSING_BATCH_BATCHID_SECURITIES_GET, { batchId }),
		{
			signal,
			headers: { 'deal-id': dealId },
		}
	);

// --- People ---

export interface BatchPersonItem {
	extractedName: string;
	matchStatus: BatchPersonMatchStatus;
	entryCount: number;
	documentCount: number;
	matchedPersonId: string | null;
	matchedPersonName: string | null;
	reviewStatus: 'needs_review' | 'merged';
}

export interface GetBatchPeopleResponse {
	people: BatchPersonItem[];
	total: number;
	statusCounts: { matched: number; new: number; unmatched: number; potential_match: number };
	fetchErrors?: DocumentFetchError[];
}

export const getBatchPeople = (
	batchId: string,
	dealId: string,
	signal?: AbortSignal
): Promise<GetBatchPeopleResponse> =>
	get<GetBatchPeopleResponse>(
		formatUnicorn(API_PATHS.DOCUMENT_PARSING_BATCH_BATCHID_PEOPLE_GET, { batchId }),
		{
			signal,
			headers: { 'deal-id': dealId },
		}
	);

export const matchBatchPerson = (params: {
	batchId: string;
	dealId: string;
	extractedName: string;
	matchedPersonId: string | null;
}): Promise<{ success: boolean }> =>
	post<{ success: boolean }>(
		formatUnicorn(API_PATHS.DOCUMENT_PARSING_BATCH_BATCHID_PEOPLE_MATCH_POST, {
			batchId: params.batchId,
		}),
		{
			extractedName: params.extractedName,
			matchedPersonId: params.matchedPersonId,
		},
		{ 'deal-id': params.dealId }
	);

export const matchBatchSecurity = (params: {
	batchId: string;
	dealId: string;
	extractedName: string;
	issuerPersonId: string | null;
	matchedSecurityId: string | null;
}): Promise<{ success: boolean }> =>
	post<{ success: boolean }>(
		formatUnicorn(API_PATHS.DOCUMENT_PARSING_BATCH_BATCHID_SECURITIES_MATCH_POST, {
			batchId: params.batchId,
		}),
		{
			extractedName: params.extractedName,
			issuerPersonId: params.issuerPersonId,
			matchedSecurityId: params.matchedSecurityId,
		},
		{ 'deal-id': params.dealId }
	);

export interface ReextractBatchRequest {
	batchId: string;
	dealId: string;
	customSystemPrompt: string;
}

export const reextractBatch = (params: ReextractBatchRequest): Promise<{ batchId: string }> =>
	post<{ batchId: string }>(
		formatUnicorn(API_PATHS.DOCUMENT_PARSING_BATCH_BATCHID_REEXTRACT_POST, {
			batchId: params.batchId,
		}),
		{ customSystemPrompt: params.customSystemPrompt },
		{ 'deal-id': params.dealId }
	);
